import { useAuth } from "@/context/AuthContext";
import ContactProductTags, { PRODUCT_TAGS, tagsOf } from "@/components/ContactProductTags";
import QuickReplies from "@/components/QuickReplies";
import React, { useEffect, useState } from "react";
import ContactFollowup, { WaitingTime, returnStatus } from "@/components/ContactFollowup";
import InternalNotes from "@/components/InternalNotes";
import { api } from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, Pin, Send, User, Loader2, Search, MessageCircle, Paperclip, Camera, Phone, Video, X, Sparkles, Mic, Square } from "lucide-react";

const FUNNEL_STAGES = ["Novo", "Em atendimento", "Em negociação", "Proposta fechada", "Follow-up", "Venda ganha", "Venda perdida"];
const stageOf = (contact) => contact?.stage || "Novo";
const responseLabel = (contact) => contact?.last_direction === "in" ? "Precisa de resposta" : contact?.last_direction === "out" ? "Aguardando cliente" : "Sem mensagens";

const allowedTypes = new Set([
  "image/jpeg", "image/png", "image/webp", "application/pdf", "text/plain",
  "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

const readBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(",")[1]);
  reader.onerror = () => reject(new Error("Falha ao ler arquivo"));
  reader.readAsDataURL(file);
});

export default function ContactsPage() {
  const [searchParams] = useSearchParams();
  const telefoneSolicitado = searchParams.get("phone");
  const telefonePendente = React.useRef(telefoneSolicitado);
  useEffect(() => { telefonePendente.current = telefoneSolicitado; }, [telefoneSolicitado]);
  const [contacts, setContacts] = useState([]);
  const radarRequested = searchParams.get("radar") === "1";
  const requestedProduct = searchParams.get("product");
  const [productFilter, setProductFilter] = useState(() =>
    radarRequested && PRODUCT_TAGS.includes(requestedProduct) ? requestedProduct : ""
  );
  const [stageFilter, setStageFilter] = useState("");
  const [responseFilter, setResponseFilter] = useState("");
  const [returnFilter, setReturnFilter] = useState("");
  const [crmNow, setCrmNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setCrmNow(Date.now()), 30000); return () => clearInterval(t); }, []);
  const [stageSaving, setStageSaving] = useState(null);
  const [pinSaving, setPinSaving] = useState(false);
  const [footerTool, setFooterTool] = useState("");

  const stageLock = React.useRef(false);
  const contactsEpoch = React.useRef(0);
  const changeStage = async (contact, stage) => {
    if (!contact?.phone || stageLock.current || stage === stageOf(contact)) return;
    const phone = contact.phone;
    stageLock.current = true;
    contactsEpoch.current += 1;
    setStageSaving(phone);
    try {
      await api.put("/contacts/funnel-stage", { phone, stage });
      setContacts(items => items.map(item => item.phone === phone ? { ...item, stage } : item));
      setSelected(item => item?.phone === phone ? { ...item, stage } : item);
      toast.success("Etapa salva");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Não foi possível salvar a etapa");
    } finally {
      contactsEpoch.current += 1;
      stageLock.current = false;
      setStageSaving(null);
    }
  };
  const [selected, setSelected] = useState(null);
  const [messageSearchOpen, setMessageSearchOpen] = useState(false);
  const [messageSearch, setMessageSearch] = useState("");
  const [messageMatch, setMessageMatch] = useState(0);
  const searchInputRef = React.useRef(null);
  const messageNodes = React.useRef(new Map());
  useEffect(() => {
    setMessageSearchOpen(false); setMessageSearch(""); setMessageMatch(0);
  }, [selected?.phone]);
  useEffect(() => {
    if (messageSearchOpen) searchInputRef.current?.focus();
  }, [messageSearchOpen]);

  useEffect(() => { setFooterTool(""); }, [selected?.phone]);
  const togglePin = async (contact) => {
    if (!contact?.phone || stageLock.current) return;
    const phone = contact.phone;
    stageLock.current = true;
    contactsEpoch.current += 1;
    setPinSaving(true);
    try {
      const { data } = await api.put("/contacts/pin", { phone, pinned: contact.pinned !== true });
      setContacts(items => items.map(item => item.phone === phone ? { ...item, pinned: data.pinned } : item));
      setSelected(item => item?.phone === phone ? { ...item, pinned: data.pinned } : item);
      toast.success(data.pinned ? "Conversa fixada" : "Conversa desafixada");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Não foi possível alterar a fixação");
    } finally {
      contactsEpoch.current += 1;
      stageLock.current = false;
      setPinSaving(false);
    }
  };

  const saveProductTags = async (phone, product_tags) => {
    if (stageLock.current) throw new Error("Aguarde a gravação atual");
    stageLock.current = true;
    contactsEpoch.current += 1;
    try {
      const { data } = await api.put("/contacts/product-tags", { phone, product_tags });
      setContacts(items => items.map(item => item.phone === phone ? { ...item, product_tags: data.product_tags } : item));
      setSelected(item => item?.phone === phone ? { ...item, product_tags: data.product_tags } : item);
    } finally {
      contactsEpoch.current += 1;
      stageLock.current = false;
    }
  };

  const activePhone = React.useRef(null);
  activePhone.current = selected?.phone || null;
  const [contactEditor, setContactEditor] = useState(null);
  const [contactSaving, setContactSaving] = useState(false);
  const saveContactName = async (event) => {
    event.preventDefault();
    if (!contactEditor || contactSaving) return;
    const { phone } = contactEditor;
    const name = contactEditor.name.trim();
    if (!name) return toast.error("Informe o nome do contato");
    setContactSaving(true);
    try {
      const { data } = await api.put("/contacts/name", { phone, name });
      setContacts((items) => items.map((item) => item.phone === phone ? { ...item, name: data.name } : item));
      setSelected((item) => item?.phone === phone ? { ...item, name: data.name } : item);
      setContactEditor(null);
      toast.success("Contato salvo no sistema");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Não foi possível salvar o contato");
    } finally {
      setContactSaving(false);
    }
  };
  useEffect(() => { setContactEditor(null); }, [selected?.phone]);
  const [activity, setActivity] = useState({});
  const [recipientOverrides, setRecipientOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem("whatsapp-recipient-overrides") || "{}"); }
    catch { return {}; }
  });
  const [recipientDraft, setRecipientDraft] = useState({ phone: "", value: "" });
  const recipientFor = (phone) => {
    const original = String(phone || "").replace(/\D/g, "");
    return recipientOverrides[original] || original;
  };
  const saveRecipient = () => {
    const original = String(selected?.phone || "").replace(/\D/g, "");
    if (recipientDraft.phone !== original) {
      toast.error("Confira o telefone deste contato antes de salvar");
      return;
    }
    const corrected = recipientDraft.value.replace(/\D/g, "");
    if (!original || !/^\d{12,13}$/.test(corrected) || corrected === original) {
      toast.error("Informe o telefone completo com país e DDD");
      return;
    }
    if (Object.entries(recipientOverrides).some(([phone, number]) => phone !== original && number === corrected)) {
      toast.error("Este telefone já está vinculado a outra conversa");
      return;
    }
    const next = { ...recipientOverrides, [original]: corrected };
    localStorage.setItem("whatsapp-recipient-overrides", JSON.stringify(next));
    setRecipientOverrides(next);
    toast.success("Número de envio salvo neste navegador");
  };
  const removeRecipient = () => {
    const original = String(selected?.phone || "").replace(/\D/g, "");
    if (!original) return;
    const next = { ...recipientOverrides };
    delete next[original];
    localStorage.setItem("whatsapp-recipient-overrides", JSON.stringify(next));
    setRecipientOverrides(next);
    setRecipientDraft({ phone: original, value: "" });
    toast.success("Número removido desta conversa");
  };
  const [messages, setMessages] = useState([]);
  const [messagesOwner, setMessagesOwner] = useState(null);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState("");
  const conversationKey = JSON.stringify([selected?.phone || "", recipientFor(selected?.phone)]);
  const requestScope = React.useRef({ key: null, generation: 0, issued: 0, applied: 0 });
  if (requestScope.current.key !== conversationKey) {
    requestScope.current = { key: conversationKey, generation: requestScope.current.generation + 1, issued: 0, applied: 0 };
  }
  const [incomingAudio, setIncomingAudio] = useState({});
  const [incomingImages, setIncomingImages] = useState({});
  const audioUrls = React.useRef({});
  const audioLoading = React.useRef(new Set());
  const mediaRetryAt = React.useRef({});
  useEffect(() => () => {
    Object.values(audioUrls.current).forEach((url) => URL.revokeObjectURL(url));
  }, []);
  useEffect(() => {
    for (const message of [...messages].reverse()) {
      if (!["audio", "image", "video"].includes(message.type) || !message.id ||
          audioUrls.current[message.id] || audioLoading.current.has(message.id) || audioLoading.current.size >= 4 || (mediaRetryAt.current[message.id] || 0) > Date.now()) continue;
      audioLoading.current.add(message.id);
      api.get(`/messages/${message.id}/${message.type}`, { responseType: "blob", timeout: message.type === "video" ? 60000 : 30000 }).then(({ data }) => {
        const url = URL.createObjectURL(data);
        audioUrls.current[message.id] = url;
        if (["image", "video"].includes(message.type)) setIncomingImages((current) => ({ ...current, [message.id]: url }));
        else setIncomingAudio((current) => ({ ...current, [message.id]: url }));
      }).catch((error) => { mediaRetryAt.current[message.id] = Date.now() + (error.response?.status === 404 ? 300000 : 30000); }).finally(() => audioLoading.current.delete(message.id));
    }
  }, [messages]);
  const messagesScrollRef = React.useRef(null);
  const [internalNotes, setInternalNotes] = useState([]);
  const [conversationTab, setConversationTab] = useState("messages");
  useEffect(() => { setConversationTab("messages"); }, [selected?.phone]);
  const { user } = useAuth();
  const draftKey = user?.id ? "feedbackhub:drafts:" + user.id : null;
  const [draftState, setDraftState] = useState(null);
  const draftRef = React.useRef(null);
  function readDrafts() {
    if (!draftKey) return {};
    try { return JSON.parse(localStorage.getItem(draftKey) || "{}") || {}; }
    catch { return {}; }
  }
  const drafts = draftState?.key === draftKey ? draftState.values : readDrafts();
  const phoneForDraft = selected?.phone;
  const text = drafts[phoneForDraft] || "";
  const setText = (value, expected) => {
    if (!phoneForDraft || !draftKey) return;
    const current = draftRef.current?.key === draftKey ? draftRef.current.values : readDrafts();
    if (expected !== undefined && (current[phoneForDraft] || "") !== expected) return;
    const next = {...current};
    const updated = typeof value === "function" ? value(current[phoneForDraft] || "") : value;
    if (updated) next[phoneForDraft] = updated;
    else delete next[phoneForDraft];
    const state = {key: draftKey, values: next};
    draftRef.current = state;
    setDraftState(state);
    try { localStorage.setItem(draftKey, JSON.stringify(next)); }
    catch { /* Continue using the draft in memory. */ }
  };
  const [quote, setQuote] = useState(null);
  const activeQuote = quote?.scope === conversationKey ? quote : null;
  useEffect(() => { setQuote(null); }, [conversationKey]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [newConversationOpen, setNewConversationOpen] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [openingConversation, setOpeningConversation] = useState(false);
  const openNewConversation = async (event) => {
    event.preventDefault();
    if (openingConversation || sending || recording) return;
    const raw = newPhone.trim();
    let phone = raw.replace(/\D/g, "");
    if (!raw.startsWith("+") && (phone.length === 10 || phone.length === 11)) phone = "55" + phone;
    if (!/^[1-9]\d{7,14}$/.test(phone) ||
        (!raw.startsWith("+") && !/^55\d{10,11}$/.test(phone))) {
      toast.error("Informe DDD e telefone. Para outro país, use + e o código do país.");
      return;
    }
    setOpeningConversation(true);
    try {
      const { data } = await api.post("/contacts/start", { phone });
      telefonePendente.current = null;
      setContacts((current) => [data, ...current.filter((item) => item.phone !== data.phone)]);
      setMessages([]);
      setInternalNotes([]);
      setAttachment(null);
      setVoiceNote(null);
      setSelected(data);
      setSearch("");
      setNewConversationOpen(false);
      setNewPhone("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Não foi possível abrir a conversa");
    } finally {
      setOpeningConversation(false);
    }
  };
  const [attachment, setAttachment] = useState(null);
  const [recording, setRecording] = useState(false);
  const [voiceNote, setVoiceNote] = useState(null);
  const recorderRef = React.useRef(null);
  const voiceStreamRef = React.useRef(null);
  const fileInput = React.useRef(null);
  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [callRoom, setCallRoom] = useState(null);
  const [callError, setCallError] = useState("");
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [callLoading, setCallLoading] = useState(false);

  const closeCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOpen(false);
  };

  useEffect(() => () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    recorderRef.current?.state === "recording" && recorderRef.current.stop();
    voiceStreamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  useEffect(() => () => {
    if (voiceNote?.url) URL.revokeObjectURL(voiceNote.url);
  }, [voiceNote]);

  const startRecording = async () => {
    if (!selected?.phone || !navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      toast.error("Gravação de áudio indisponível neste navegador");
      return;
    }
    const mimeType = ["audio/ogg;codecs=opus", "audio/webm;codecs=opus", "audio/mp4"]
      .find((type) => MediaRecorder.isTypeSupported(type));
    if (!mimeType) return toast.error("Formato de áudio não suportado neste navegador");
    try {
      const recordedPhone = selected.phone;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      voiceStreamRef.current = stream;
      const chunks = [];
      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        voiceStreamRef.current = null;
        setRecording(false);
        const blob = new Blob(chunks, { type: mimeType });
        if (!blob.size) return toast.error("O áudio ficou vazio. Grave novamente.");
        if (blob.size > 8 * 1024 * 1024) return toast.error("O áudio deve ter no máximo 8 MB");
        setVoiceNote({ phone: recordedPhone, file: new File([blob], `audio-${Date.now()}.${mimeType.includes("ogg") ? "ogg" : mimeType.includes("mp4") ? "m4a" : "webm"}`, { type: mimeType.split(";")[0] }), url: URL.createObjectURL(blob) });
      };
      recorder.start();
      setRecording(true);
    } catch {
      toast.error("Não foi possível acessar o microfone. Verifique a permissão no navegador.");
    }
  };

  const sendVoiceNote = async () => {
    if (!selected?.phone || !voiceNote || voiceNote.phone !== selected.phone || sending) return;
    const to = recipientFor(selected.phone);
    if (to.length >= 14) return toast.error("Confirme o telefone real antes de enviar áudio");
    if (!to) return;
    setSending(true);
    try {
      await api.post("/whatsapp/send-media", {
        to, filename: voiceNote.file.name, mime_type: voiceNote.file.type,
        data: await readBase64(voiceNote.file), caption: "",
      });
      setVoiceNote(null);
      toast.success("Áudio aceito pelo conector. Confira a entrega no WhatsApp.");
      setTimeout(() => { loadMessages(selected.phone); loadContacts(); }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Não foi possível enviar o áudio");
    } finally {
      setSending(false);
    }
  };

  const openCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Este navegador não permite abrir a câmera nesta página");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      streamRef.current = stream;
      setCameraOpen(true);
    } catch {
      toast.error("Não foi possível abrir a câmera. Verifique a permissão no navegador");
    }
  };

  const takePhoto = () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video?.videoHeight) {
      toast.error("Aguarde a imagem da câmera aparecer");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) selectAttachment(new File([blob], `foto-${Date.now()}.jpg`, { type: "image/jpeg" }));
      else toast.error("Não foi possível capturar a foto");
      closeCamera();
    }, "image/jpeg", 0.85);
  };

  useEffect(() => {
    if (cameraOpen && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => toast.error("Não foi possível mostrar a câmera"));
    }
  }, [cameraOpen]);

  const selectAttachment = (file) => {
    if (!file) return;
    if (!allowedTypes.has(file.type)) return toast.error("Tipo de arquivo não permitido");
    if (!file.size || file.size > 8 * 1024 * 1024) return toast.error("O arquivo deve ter no máximo 8 MB");
    setAttachment(file);
  };

  const isRealContact = (contact) => {
    const phone = String(contact?.phone || "").toLowerCase();

    return (
      phone &&
      !phone.includes("@newsletter") &&
      !phone.includes("@g.us") &&
      phone !== "status"
    );
  };

  const contactTime = (contact) => Math.max(
    Date.parse(contact.last_message_at || "") || 0,
    Date.parse(activity[contact.phone] || "") || 0
  );
  const cleanContacts = contacts.filter(isRealContact).sort((a, b) =>
    Number(b.pinned === true) - Number(a.pinned === true) ||
    contactTime(b) - contactTime(a) || String(a.id || a.phone).localeCompare(String(b.id || b.phone))
  );

  const [reviews, setReviews] = useState({});
  const hasReview = phone => Object.prototype.hasOwnProperty.call(reviews, phone);
  const [reviewOnly, setReviewOnly] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  useEffect(() => {
    let live = true;
    api.get("/contacts/review").then(({data}) => { if (live) setReviews(data); }).catch(() => toast.error("Não foi possível carregar as revisões."));
    return () => { live = false; };
  }, []);
  async function toggleReview(phone, reason) {
    const review = reason !== undefined || !hasReview(phone);
    setReviewBusy(true);
    try {
      await api.put("/contacts/review", {phone, review, reason: reason ?? ""});
      setReviews(items => {
        const next = {...items};
        if (review) next[phone] = reason ?? "";
        else delete next[phone];
        return next;
      });
    } catch { toast.error("Não foi possível salvar a revisão."); }
    finally { setReviewBusy(false); }
  }
  const [archives, setArchives] = useState({});
  const [radarOnly, setRadarOnly] = useState(radarRequested);
  const [archiveOnly, setArchiveOnly] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const archiveRevision = React.useRef(0);
  const isArchived = phone => Object.prototype.hasOwnProperty.call(archives, phone);
  useEffect(() => {
    let live = true;
    async function refresh() {
      const revision = archiveRevision.current;
      try {
        const {data} = await api.get("/contacts/archive");
        if (live && revision === archiveRevision.current) setArchives(data);
      } catch { /* Retry on the next refresh. */ }
    }
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => { live = false; clearInterval(timer); };
  }, []);
  async function toggleArchive(phone) {
    const archived = !isArchived(phone);
    archiveRevision.current++;
    setArchiveBusy(true);
    try {
      const {data} = await api.put("/contacts/archive", {phone, archived});
      archiveRevision.current++;
      setArchives(current => {
        const next = {...current};
        if (archived) next[phone] = data.archived_at;
        else delete next[phone];
        return next;
      });
      toast.success(archived ? "Conversa arquivada" : "Conversa desarquivada");
    } catch { toast.error("Não foi possível atualizar o arquivo."); }
    finally { setArchiveBusy(false); }
  }
  const filteredContacts = cleanContacts.filter((contact) => {
    if (radarOnly && !contact.proposal_stalled) return false;
    if (isArchived(contact.phone) !== archiveOnly) return false;
    if (reviewOnly && !hasReview(contact.phone)) return false;
    if (productFilter === "none" ? tagsOf(contact).length > 0 : productFilter && !tagsOf(contact).includes(productFilter)) return false;
    const followup = returnStatus(contact, crmNow);
    if (returnFilter === "today" && (!contact.return_at || new Date(contact.return_at).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" }) !== new Date(crmNow).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" }))) return false;
    if (returnFilter === "overdue" && followup !== "overdue") return false;
    if (stageFilter && stageOf(contact) !== stageFilter) return false;
    if (responseFilter && contact.last_direction !== responseFilter) return false;
    const term = search.trim().toLowerCase();

    if (!term) return true;

    return (
      String(contact.name || "").toLowerCase().includes(term) ||
      String(contact.phone || "").toLowerCase().includes(term)
    );
  });

  const openCall = async (kind) => {
    if (callLoading || callRoom) return;
    const to = String(selected?.phone || "").replace(/\D/g, "");
    if (!to) return toast.error("Este contato não tem telefone válido");
    setCallLoading(true);
    setCallError("");
    setInviteSent(false);
    try {
      const { data } = await api.post("/calls", { to, kind });
      setCallRoom({ ...data, kind, to });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível criar a chamada");
    } finally {
      setCallLoading(false);
    }
  };

  const sendCallInvite = async () => {
    if (!callRoom || inviteSending || inviteSent) return;
    setInviteSending(true);
    try {
      await api.post("/whatsapp/send", {
        to: callRoom.to,
        text: `Convite para ${callRoom.kind === "voice" ? "ligação de voz" : "videochamada"}: ${callRoom.roomUrl}`,
      });
      setInviteSent(true);
      toast.success("Convite enviado pelo WhatsApp");
      loadMessages(callRoom.to);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar o convite");
    } finally {
      setInviteSending(false);
    }
  };

  const loadContacts = async () => {
    if (stageLock.current) return;
    const epoch = contactsEpoch.current;
    try {
      const { data } = await api.get("/contacts");
      if (stageLock.current || epoch !== contactsEpoch.current) return;
      const list = Array.isArray(data) ? data : [];

      setContacts(list);

      if (telefonePendente.current) {
        const esperado = String(telefonePendente.current).replace(/\D/g, "");
        const encontrado = list.find((item) =>
          isRealContact(item) && String(item.phone).replace(/\D/g, "") === esperado
        );
        telefonePendente.current = null;
        setSelected(encontrado || null);
        if (!encontrado) toast.error("Conversa deste cliente não encontrada nesta conta");
        return;
      }

      setSelected((current) => {
        if (current) {
          const updated = list.find(
            (item) =>
              item.id === current.id ||
              item.phone === current.phone
          );

          if (updated && isRealContact(updated)) {
            return updated;
          }
        }

        return list.find(isRealContact) || null;
      });
    } catch {
      // mantém a tela aberta em falhas temporárias
    }
  };

  const loadMessages = async (phone) => {
    if (!phone || activePhone.current !== phone) return;
    const scope = requestScope.current;
    const sequence = ++scope.issued;
    const isCurrent = () => requestScope.current === scope && activePhone.current === phone && sequence >= scope.applied;
    try {
      const recipient = recipientFor(phone);
      const query = new URLSearchParams({ phone });
      if (recipient !== String(phone).replace(/\D/g, "")) query.set("recipient", recipient);
      const { data } = await api.get(`/messages?${query.toString()}`, { timeout: 15000 });
      if (!isCurrent()) return;
      if (!Array.isArray(data)) throw new Error("Resposta de mensagens inválida");
      scope.applied = sequence;
      setMessagesOwner(scope.key);
      setMessages(data);
      setMessagesError("");
      const latest = data.reduce((value, item) =>
        (Date.parse(item.created_at || "") || 0) > (Date.parse(value || "") || 0) ? item.created_at : value, "");
      if (latest) setActivity((current) => ({ ...current, [phone]: latest }));
    } catch {
      if (!isCurrent()) return;
      scope.applied = sequence;
      setMessagesOwner(scope.key);
      setMessages([]);
      setMessagesError("Não foi possível carregar esta conversa. Tentando novamente…");
    } finally {
      if (isCurrent()) setMessagesLoading(false);
    }
  };

  const loadInternalNotes = async (phone) => {
    if (!phone || activePhone.current !== phone) return;
    const scope = requestScope.current;
    try {
      const { data } = await api.get(`/internal-notes?phone=${encodeURIComponent(phone)}`, { timeout: 15000 });
      if (requestScope.current !== scope || activePhone.current !== phone) return;
      setInternalNotes(Array.isArray(data) ? data : []);
    } catch {
      if (requestScope.current === scope && activePhone.current === phone) setInternalNotes([]);
    }
  };

  useEffect(() => {
    loadContacts();

    const timer = setInterval(loadContacts, 5000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setShowSuggestions(false);
    setSuggestions([]);
    setMessages([]);
    setMessagesOwner(null);
    setInternalNotes([]);
    setMessagesError("");
    setMessagesLoading(Boolean(selected?.phone));
    if (!selected?.phone) {
      setMessages([]);
      setInternalNotes([]);
      return;
    }

    loadMessages(selected.phone);
    loadInternalNotes(selected.phone);

    const timer = setInterval(() => {
      loadMessages(selected.phone);
      loadInternalNotes(selected.phone);
    }, 3000);

    return () => clearInterval(timer);
  }, [selected?.phone, recipientOverrides]);

  const send = async () => {
    if (!selected?.phone || (!text.trim() && !attachment)) return;

    const to = recipientFor(selected.phone);
    if (to.length >= 14) return toast.error("Confirme o telefone real antes de enviar");

    if (!to) return;

    if (attachment && activeQuote) return toast.error("Para enviar um anexo, remova a citação primeiro.");
    setSending(true);

    try {
      if (attachment) {
        await api.post("/whatsapp/send-media", {
          to, filename: attachment.name, mime_type: attachment.type,
          data: await readBase64(attachment), caption: text.trim(),
        });
        setAttachment(null);
      } else {
        await api.post("/whatsapp/send", { to, text: text.trim(), conversation_phone: selected.phone, reply_to: activeQuote?.id });
      }

      setText("", text);
      setQuote(current => current === activeQuote ? null : current);
      toast.success("Envio aceito pelo conector. Confira a entrega no WhatsApp.");

      setTimeout(() => {
        loadMessages(selected.phone);
        loadContacts();
      }, 1000);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Falha ao enviar"
      );
    } finally {
      setSending(false);
    }
  };

  const visibleMessages = (messagesOwner === conversationKey ? messages : []).filter(
    (message) =>
      message.text ||
      message.type !== "text"
  );
  const messageSearchTerm = messageSearch.trim().toLocaleLowerCase('pt-BR');
  const matchingMessageIndexes = messageSearchOpen && messageSearchTerm ? visibleMessages.reduce((found, message, index) => {
    if (String(message.text || '').toLocaleLowerCase('pt-BR').includes(messageSearchTerm)) found.push(index);
    return found;
  }, []) : [];
  const safeMessageMatch = matchingMessageIndexes.length ? Math.min(messageMatch, matchingMessageIndexes.length - 1) : 0;
  const activeMessageIndex = matchingMessageIndexes[safeMessageMatch];
  const matchedMessageSet = new Set(matchingMessageIndexes);
  const navigateMessageMatch = direction => {
    if (matchingMessageIndexes.length) setMessageMatch((safeMessageMatch + direction + matchingMessageIndexes.length) % matchingMessageIndexes.length);
  };
  useEffect(() => {
    if (messageSearchOpen && activeMessageIndex !== undefined) {
      const pane = messagesScrollRef.current;
      const node = messageNodes.current.get(activeMessageIndex);
      if (pane && node) {
        const paneRect = pane.getBoundingClientRect();
        const nodeRect = node.getBoundingClientRect();
        pane.scrollTop += nodeRect.top - paneRect.top - pane.clientHeight / 2 + nodeRect.height / 2;
      }
    }
  }, [messageSearchOpen, messageSearch, activeMessageIndex, conversationTab]);
  useEffect(() => {
    const pane = messagesScrollRef.current;
    if (pane && !(messageSearchOpen && messageSearchTerm)) pane.scrollTop = pane.scrollHeight;
  }, [selected?.phone, visibleMessages.length, visibleMessages[visibleMessages.length - 1]?.id, messageSearchOpen, messageSearchTerm]);
  const latestMessage = visibleMessages[visibleMessages.length - 1];
  const canSuggest = Boolean(selected?.phone && latestMessage?.direction !== "out" && latestMessage?.text?.trim());

  const generateSuggestions = async () => {
    if (!canSuggest || suggestionsLoading) return;
    const phone = selected.phone;
    setSuggestionsLoading(true);
    setSuggestions([]);
    setShowSuggestions(false);
    try {
      const { data } = await api.post("/ai/suggest-replies", { phone });
      if (selected.phone === phone) {
        setSuggestions(data.suggestions || []);
        setShowSuggestions(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível gerar sugestões com IA");
    } finally {
      setSuggestionsLoading(false);
    }
  };

  return (
    <Card className="h-[calc(100vh-155px)] min-h-[560px] bg-[#0b1220] border-pink-500/30 overflow-hidden">
      {contactEditor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <form onSubmit={saveContactName} role="dialog" aria-modal="true" aria-labelledby="contact-name-title" className="w-full max-w-sm rounded-xl border border-pink-500/30 bg-slate-950 p-5 space-y-4">
            <h2 id="contact-name-title" className="font-semibold text-pink-300">Salvar contato</h2>
            <p className="text-sm text-slate-400">+{contactEditor.phone}</p>
            <label className="block text-sm">Nome do contato
              <input autoFocus required maxLength={120} disabled={contactSaving} value={contactEditor.name} onChange={(event) => setContactEditor({ ...contactEditor, name: event.target.value })} className="mt-2 w-full rounded border border-slate-700 bg-slate-900 p-2 text-white" />
            </label>
            <p className="text-xs text-slate-400">O nome será salvo nesta conta do sistema.</p>
            <div className="flex justify-end gap-3">
              <button type="button" disabled={contactSaving} onClick={() => setContactEditor(null)}>Cancelar</button>
              <button type="submit" disabled={contactSaving} className="rounded bg-pink-500 px-4 py-2 text-white">{contactSaving ? "Salvando..." : "Salvar"}</button>
            </div>
          </form>
        </div>
      )}
      {newConversationOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <form onSubmit={openNewConversation} role="dialog" aria-modal="true" aria-labelledby="new-conversation-title"
            className="w-full max-w-md rounded-xl border border-pink-500/40 bg-[#0b1220] p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 id="new-conversation-title" className="font-bold text-lg">Nova conversa</h2>
              <Button type="button" variant="ghost" size="icon" aria-label="Fechar nova conversa"
                disabled={openingConversation} onClick={() => setNewConversationOpen(false)}><X className="w-5 h-5" /></Button>
            </div>
            <label htmlFor="new-conversation-phone" className="block text-sm">Número do WhatsApp</label>
            <input id="new-conversation-phone" type="tel" autoFocus autoComplete="off" required maxLength={30}
              value={newPhone} onChange={(event) => setNewPhone(event.target.value)} disabled={openingConversation}
              placeholder="(11) 91234-5678"
              className="w-full h-11 px-3 rounded-lg bg-slate-900 border border-slate-700 outline-none focus:border-pink-500" />
            <p className="text-xs text-slate-400">Brasil: DDD e telefone. Outro país: +código do país e telefone. Não precisa salvar na agenda.</p>
            <Button type="submit" disabled={openingConversation || !newPhone.trim()} className="w-full bg-pink-500 hover:bg-pink-600">
              {openingConversation ? "Abrindo..." : "Abrir conversa"}
            </Button>
          </form>
        </div>
      )}
      {cameraOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          <div className="w-full max-w-xl rounded-xl border border-pink-500/40 bg-[#0b1220] p-4 space-y-4">
            <div className="flex items-center justify-between text-white font-semibold">
              <span>Tirar foto</span>
              <Button type="button" variant="ghost" onClick={closeCamera} aria-label="Fechar câmera"><X className="w-5 h-5" /></Button>
            </div>
            <video ref={videoRef} autoPlay playsInline muted className="w-full aspect-video bg-black rounded-lg object-contain" />
            <Button type="button" className="w-full bg-pink-500 hover:bg-pink-600" onClick={takePhoto}>
              <Camera className="w-4 h-4 mr-2" /> Capturar foto
            </Button>
          </div>
        </div>
      )}
      {callRoom && (
        <div role="dialog" aria-modal="true" aria-label="Chamada" className="fixed inset-0 z-50 bg-black/95 flex flex-col p-3 md:p-6">
          <div className="flex flex-wrap items-center gap-3 pb-3 text-white">
            <strong>{callRoom.kind === "voice" ? "Ligação de voz" : "Videochamada"}</strong>
            <span className="text-xs text-slate-300">O cliente participa pelo link enviado no WhatsApp.</span>
            <Button type="button" onClick={sendCallInvite} disabled={inviteSending || inviteSent} className="ml-auto bg-pink-500 hover:bg-pink-600">
              {inviteSending ? "Enviando..." : inviteSent ? "Convite enviado" : "Enviar convite pelo WhatsApp"}
            </Button>
            <Button type="button" variant="outline" onClick={() => setCallRoom(null)}>Encerrar</Button>
          </div>
          {callError && <p className="text-red-300 p-3">{callError}</p>}
          <iframe
            title="Chamada com cliente"
            src={`${callRoom.hostRoomUrl}${callRoom.hostRoomUrl.includes("?") ? "&" : "?"}lang=pt${callRoom.kind === "voice" ? "&video=off" : ""}`}
            allow="camera; microphone; fullscreen; display-capture; autoplay; compute-pressure"
            className="flex-1 w-full min-h-0 rounded-xl overflow-hidden bg-[#0b1220]"
          />
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] h-full">

        {/* CONVERSAS À ESQUERDA */}
        <div className="border-r border-pink-500/20 flex flex-col min-h-0 bg-[#0d1525]">
          <div className="p-4 border-b border-slate-800">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-bold text-lg">
                  Conversas e funil
                </div>

                <div className="text-xs text-slate-500">
                  {filteredContacts.length} de {cleanContacts.length} conversas
                </div>
              </div>

              <div className="w-9 h-9 rounded-full bg-pink-500/10 border border-pink-500/30 grid place-items-center">
                <MessageCircle className="w-4 h-4 text-pink-400" />
              </div>
            </div>

            <Button type="button" className="w-full mb-3 bg-pink-500 hover:bg-pink-600"
              disabled={sending || recording || Boolean(callRoom)}
              onClick={() => { setNewPhone(""); setNewConversationOpen(true); }}>
              <MessageCircle className="w-4 h-4 mr-2" /> Nova conversa
            </Button>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />

              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Pesquisar conversa"
                placeholder="Pesquisar conversa..."
                className="w-full h-10 pl-9 pr-3 rounded-lg bg-slate-900 border border-slate-800 text-sm outline-none focus:border-pink-500/50"
              />
            </div>
            <div className="relative mt-2">
            <details open={radarRequested || undefined}>
              <summary className="cursor-pointer rounded-lg border border-slate-700 bg-slate-900 pl-3 pr-28 py-1.5 text-xs">
                Filtros {([responseFilter, returnFilter, productFilter, stageFilter, reviewOnly, archiveOnly, radarOnly].filter(Boolean).length || "")}
              </summary>
              <div className="mt-2 space-y-2">
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={archiveOnly} onChange={event => { setArchiveOnly(event.target.checked); if (event.target.checked) setRadarOnly(false); }} />Arquivadas</label>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={radarOnly} onChange={event => { setRadarOnly(event.target.checked); if (event.target.checked) setArchiveOnly(false); }} />Propostas paradas</label>
              <select aria-label="Filtrar por resposta" value={responseFilter} onChange={e => setResponseFilter(e.target.value)} className="w-full rounded-lg border border-slate-700 bg-slate-900 p-2 text-xs">
                <option value="">Todas as respostas</option>
                <option value="in">Precisam de resposta</option>
                <option value="out">Aguardando cliente</option>
              </select>
              <select aria-label="Filtrar retornos" value={returnFilter} onChange={e => setReturnFilter(e.target.value)} className="w-full rounded-lg border border-slate-700 bg-slate-900 p-2 text-xs">
                <option value="">Todos os retornos</option><option value="today">Retornos de hoje</option><option value="overdue">Retornos atrasados</option>
              </select>
              <select aria-label="Filtrar por produto" value={productFilter} onChange={e => setProductFilter(e.target.value)} className="w-full rounded-lg border border-slate-700 bg-slate-900 p-2 text-xs">
                <option value="">Todos os produtos</option><option value="none">Sem produto</option>
                {PRODUCT_TAGS.map(tag => <option key={tag} value={tag}>{tag}</option>)}
              </select>
              <select aria-label="Filtrar por etapa" value={stageFilter} onChange={e => setStageFilter(e.target.value)} className="w-full rounded-lg border border-slate-700 bg-slate-900 p-2 text-xs">
                <option value="">Todas as etapas</option>
                {FUNNEL_STAGES.map(stage => <option key={stage} value={stage}>{stage}</option>)}
              </select>
            <button type="button" aria-pressed={reviewOnly} className="m-2 rounded border border-purple-500 px-2 py-1 text-xs text-purple-200" onClick={() => setReviewOnly(!reviewOnly)}>{reviewOnly ? "Ver todas" : "Para revisar"}</button>
              </div>
            </details>
            {[responseFilter, returnFilter, productFilter, stageFilter, reviewOnly, archiveOnly, radarOnly].some(Boolean) && (
              <button type="button" className="absolute right-2 top-0 rounded px-2 py-1.5 text-xs text-pink-300 underline" onClick={() => {
                setResponseFilter("");
                setReturnFilter("");
                setProductFilter("");
                setStageFilter("");
                setReviewOnly(false);
                setArchiveOnly(false);
                setRadarOnly(false);
              }}>Limpar filtros</button>
            )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {filteredContacts.map((contact) => {
              const active =
                selected?.id === contact.id ||
                selected?.phone === contact.phone;

              return (
                <button
                  key={contact.id || contact.phone}
                  onClick={() => setSelected(contact)}
                  className={`w-full text-left p-4 border-b border-slate-800/70 transition ${
                    active
                      ? "bg-pink-500/10 border-l-2 border-l-pink-500"
                      : "hover:bg-slate-800/50 border-l-2 border-l-transparent"
                  }`}
                >
                  <div className="flex gap-3 items-center">
                    <div className="w-10 h-10 rounded-full bg-slate-800 grid place-items-center flex-none">
                      <WhatsAppContactAvatar key={contact.phone} phone={contact.phone} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-sm truncate">
                          {contact.name || contact.phone}
                          {hasReview(contact.phone) && <span className="ml-1 text-purple-400" title="Para revisar" aria-label="Para revisar">● {reviews[contact.phone]}</span>}
                          {contact.pinned === true && <Pin className="inline-block ml-1 w-3 h-3 text-pink-300" aria-label="Conversa fixada" />}
                        </div>

                        {active && (
                          <span className="w-2 h-2 rounded-full bg-pink-500 flex-none" />
                        )}
                      </div>

                      <div className="text-xs text-slate-500 truncate mt-1">
                        {drafts[contact.phone]?.trim() ? <span className="text-purple-300">Rascunho</span> : contact.last_message || `+${contact.phone}`}
                      </div>
                      <div className="mt-1 text-[10px] text-pink-300 truncate">{stageOf(contact)}</div>
                      {tagsOf(contact).length > 0 && <div className="text-[10px] text-emerald-300 truncate" title={tagsOf(contact).join(" · ")}>{tagsOf(contact).join(" · ")}</div>}
                      <div className={`text-[10px] ${contact.last_direction === "in" ? "text-amber-300" : "text-slate-400"}`}>{responseLabel(contact)}</div>
                      {contact.return_at && <div className={`text-[10px] ${returnStatus(contact, crmNow) === "overdue" ? "text-rose-300" : "text-pink-300"}`}>{returnStatus(contact, crmNow) === "overdue" ? "Retorno atrasado" : "Retorno"} · {new Date(contact.return_at).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>}
                    </div>
                  </div>
                </button>
              );
            })}

            {filteredContacts.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">
                {radarOnly && PRODUCT_TAGS.includes(productFilter) ? <>
                  <p>Nenhuma proposta parada de {productFilter} com os filtros atuais.</p>
                  <button type="button" className="mt-2 rounded border border-pink-500/50 px-3 py-1 text-pink-300 underline" onClick={() => setRadarOnly(false)}>
                    Ver conversas de {productFilter}
                  </button>
                </> : "Nenhuma conversa encontrada."}
              </div>
            )}
          </div>
        </div>

        {/* CONVERSA GRANDE À DIREITA */}
        <div className="flex flex-col min-h-0 bg-[#080e19]">
          {selected ? (
            <>
              <div className="h-[72px] px-5 border-b border-slate-800 flex items-center gap-3 flex-none">
                <div className="w-10 h-10 rounded-full bg-pink-500/10 border border-pink-500/30 grid place-items-center">
                  <WhatsAppContactAvatar key={selected.phone} phone={selected.phone} />
                </div>

                <div className="min-w-0">
                  <div className="font-semibold truncate">
                    {selected.name || selected.phone}
                  </div>

                  <div className="text-xs text-slate-500">
                    +{selected.phone}
                  </div>
                  {String(selected.phone).replace(/\D/g, "").length >= 14 && (
                    <div className="flex flex-wrap items-center gap-1 mt-1 text-xs text-amber-300">
                      <span>Telefone real para envio:</span>
                      <input aria-label="Telefone real com país e DDD" className="w-36 bg-slate-900 border border-slate-600 rounded px-1 text-white" placeholder="551120975714" value={recipientDraft.phone === String(selected.phone).replace(/\D/g, "") ? recipientDraft.value : recipientOverrides[String(selected.phone).replace(/\D/g, "")] || ""} onChange={(e) => setRecipientDraft({ phone: String(selected.phone).replace(/\D/g, ""), value: e.target.value })} />
                      <button type="button" className="underline" onClick={saveRecipient}>Salvar</button>
                      {recipientOverrides[String(selected.phone).replace(/\D/g, "")] && <button type="button" className="underline" onClick={removeRecipient}>Remover número salvo</button>}
                    </div>
                  )}
                </div>

                <button type="button" onClick={() => setContactEditor({ phone: selected.phone, name: selected.name === selected.phone ? "" : selected.name || "" })}
                  className="shrink-0 rounded border border-pink-500/40 px-2 py-1 text-xs text-pink-300 hover:bg-pink-500/10">
                  Salvar contato
                </button>
                <div className="ml-auto flex items-center gap-1">
                  <Button type="button" variant="ghost" size="icon" title="Buscar nesta conversa" aria-label="Buscar nesta conversa" aria-expanded={messageSearchOpen} onClick={() => { setConversationTab('messages'); setMessageSearchOpen(current => !current); }}>
                    <Search className="w-4 h-4 text-pink-400" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" disabled={callLoading} title="Ligação de voz no aplicativo" aria-label="Ligação de voz no aplicativo" onClick={() => openCall("voice")}>
                    <Phone className="w-4 h-4 text-pink-400" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" disabled={callLoading} title="Videochamada no aplicativo" aria-label="Videochamada no aplicativo" onClick={() => openCall("video")}>
                    <Video className="w-4 h-4 text-pink-400" />
                  </Button>

                </div>
              </div>

              {messageSearchOpen && conversationTab === 'messages' && <div className="border-b border-slate-800 px-3 py-2 flex-none">
                <div className="flex items-center gap-1">
                  <Search className="w-4 h-4 text-slate-400 flex-none" />
                  <input ref={searchInputRef} type="search" value={messageSearch} onChange={e => { setMessageSearch(e.target.value); setMessageMatch(0); }} onKeyDown={e => {
                    if (e.key === 'Escape') { setMessageSearchOpen(false); setMessageSearch(''); }
                    if (e.key === 'Enter') { e.preventDefault(); navigateMessageMatch(e.shiftKey ? -1 : 1); }
                  }} aria-label="Palavra ou valor na conversa" placeholder="Buscar palavra ou valor..." className="min-w-0 flex-1 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm outline-none focus:border-pink-500" />
                  <span role="status" aria-live="polite" className="text-xs text-slate-400 whitespace-nowrap">{messageSearchTerm ? matchingMessageIndexes.length ? `${safeMessageMatch + 1} de ${matchingMessageIndexes.length}` : 'Nenhum resultado' : ''}</span>
                  <Button type="button" variant="ghost" size="icon" disabled={!matchingMessageIndexes.length} title="Resultado anterior" aria-label="Resultado anterior" onClick={() => navigateMessageMatch(-1)}><ChevronUp className="w-4 h-4" /></Button>
                  <Button type="button" variant="ghost" size="icon" disabled={!matchingMessageIndexes.length} title="Próximo resultado" aria-label="Próximo resultado" onClick={() => navigateMessageMatch(1)}><ChevronDown className="w-4 h-4" /></Button>
                  <Button type="button" variant="ghost" size="icon" title="Fechar busca" aria-label="Fechar busca" onClick={() => { setMessageSearchOpen(false); setMessageSearch(''); }}><X className="w-4 h-4" /></Button>
                </div>
                <p className="mt-1 text-[10px] text-slate-500">Busca no texto das mensagens carregadas (até 1.000 recentes). Áudios e imagens não são transcritos.</p>
              </div>}
              {conversationTab === "notes" ? <InternalNotes key={selected.phone} phone={selected.phone} /> : <>
              <div ref={messagesScrollRef} className="flex-1 overflow-y-auto p-5 space-y-3">
                {visibleMessages.map((message, messageIndex) => (
                  <div
                    key={message.id}
                    ref={node => { if (node) messageNodes.current.set(messageIndex, node); else messageNodes.current.delete(messageIndex); }}
                    className={`flex ${
                      message.direction === "out"
                        ? "justify-end"
                        : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[72%] rounded-2xl px-4 py-2.5 text-sm ${matchedMessageSet.has(messageIndex) ? messageIndex === activeMessageIndex ? 'ring-2 ring-amber-300 ring-offset-2 ring-offset-slate-950' : 'ring-1 ring-pink-300' : ''} ${
                        message.direction === "out"
                          ? "bg-pink-500 text-white rounded-br-sm"
                          : "bg-slate-800 text-slate-100 rounded-bl-sm"
                      }`}
                    >
                      {message.message_id && <div className="flex items-start gap-2">
                        <MessageReaction message={message} />
                        <button type="button" title="Responder a esta mensagem" aria-label="Responder a esta mensagem" disabled={sending} onClick={() => setQuote({scope: conversationKey, id: message.id, preview: (message.text || ({image: "Imagem", audio: "Áudio", video: "Vídeo", document: "Documento"}[message.type]) || "Mensagem").slice(0,180)})}>↩</button>
                      </div>}
                      {message.quoted_preview && <div className="mb-1 border-l-2 border-pink-300 bg-black/20 px-2 py-1 break-words">{message.quoted_preview}</div>}
                      {message.type !== "text" && (
                        <Badge className="mb-1 bg-black/30 text-white/90 text-[10px] uppercase">
                          {message.type}
                        </Badge>
                      )}

                      {message.type === "audio" && incomingAudio[message.id] ? (
                        <audio controls src={incomingAudio[message.id]} className="max-w-full" aria-label="Ouvir áudio" />
                      ) : message.type === "video" && incomingImages[message.id] ? (
                      <div>
                        <video controls playsInline preload="metadata" src={incomingImages[message.id]} className="max-w-full max-h-72 rounded object-contain" aria-label="Vídeo da conversa" />
                        {message.text && <div className="whitespace-pre-wrap break-words mt-1">{message.text}</div>}
                      </div>
                    ) : message.type === "image" && incomingImages[message.id] ? (
                        <div><img src={incomingImages[message.id]} alt="Imagem da conversa" className="max-w-full max-h-72 rounded object-contain" />
                          {message.text && <div className="whitespace-pre-wrap break-words mt-1">{message.text}</div>}</div>
                      ) : (
                        <div className="whitespace-pre-wrap break-words">
                          {message.text || (message.type === "audio" ? "[áudio recebido]" :
                            message.type !== "text" ? `[${message.type}]` : "")}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {visibleMessages.length === 0 && (
                  <div role="status" aria-live="polite" className="h-full grid place-items-center text-slate-500 text-sm">
                    {messagesOwner !== conversationKey || messagesLoading
                      ? "Carregando conversa…"
                      : messagesError || "Nenhuma mensagem nesta conversa."}
                  </div>
                )}
              </div>

              <div className="p-3 border-t border-slate-800 flex flex-col gap-2 flex-none">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-400">
                  <span>{stageOf(selected)}</span>
                  {tagsOf(selected).length > 0 && <span className="text-pink-300">{tagsOf(selected).join(' · ')}</span>}
                  <span className={selected.last_direction === 'in' ? 'text-amber-300' : ''}>{responseLabel(selected)}</span>
                  <WaitingTime contact={selected} />
                </div>
                <div className="max-h-56 overflow-y-auto rounded-lg bg-slate-900/50" hidden={!footerTool}>
                  <div hidden={footerTool !== "stage"}>              <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-800 flex-none">
                <label className="flex items-center gap-2 text-xs text-slate-300">Etapa
                  <select aria-label="Etapa da negociação" value={stageOf(selected)} disabled={Boolean(stageSaving)} onChange={e => changeStage(selected, e.target.value)} className="max-w-full rounded-lg border border-pink-500/40 bg-slate-900 p-2 text-sm text-white disabled:opacity-50">
                    {!FUNNEL_STAGES.includes(stageOf(selected)) && <option value={stageOf(selected)}>{stageOf(selected)}</option>}
                    {FUNNEL_STAGES.map(stage => <option key={stage} value={stage}>{stage}</option>)}
                  </select>
                </label>
                <span role="status" className={`text-xs ${selected.last_direction === "in" ? "text-amber-300" : "text-slate-400"}`}>{stageSaving === selected.phone ? "Salvando etapa..." : responseLabel(selected)}</span>

              </div>
</div>
                  <div hidden={footerTool !== "products"}>              <ContactProductTags key={selected.phone} contact={selected} onSave={saveProductTags} />
</div>
                  <div hidden={footerTool !== "followup"}>              <ContactFollowup key={selected.phone} contact={selected} onSaved={values => {
                contactsEpoch.current += 1;
                const phone = selected.phone;
                setContacts(items => items.map(item => item.phone === phone ? { ...item, ...values } : item));
                setSelected(item => item?.phone === phone ? { ...item, ...values } : item);
              }} />
</div>
                  <div hidden={footerTool !== "replies"}>                <QuickReplies disabled={sending || recording} onInsert={value => setText(current => current.trim() ? current + "\n\n" + value : value)} />
</div>
                  <div hidden={footerTool !== "ai"}>                <div>
                  <Button type="button" variant="ghost" size="sm" className="text-pink-300"
                    disabled={!canSuggest || sending || suggestionsLoading}
                    onClick={generateSuggestions}>
                    {suggestionsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    {suggestionsLoading ? "Gerando respostas..." : "Gerar respostas com IA"}
                  </Button>
                  <p className="text-xs text-slate-400">Ao clicar, trechos recentes da conversa são enviados ao Google. Revise a resposta antes de enviar.</p>
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="mt-2 max-h-36 overflow-y-auto space-y-1" aria-label="Sugestões de resposta">
                      {suggestions.map((suggestion) => (
                        <button key={suggestion} type="button"
                          className="block w-full rounded-lg border border-slate-700 p-2 text-left text-sm text-slate-200 hover:border-pink-500"
                          onClick={() => { setText(suggestion); setShowSuggestions(false); }}>
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
</div>
                </div>
                <div className="flex flex-wrap items-center gap-1" aria-label="Ferramentas da conversa">
                  <input ref={fileInput} type="file" className="hidden"
                    accept="image/jpeg,image/png,image/webp,.pdf,.txt,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => { selectAttachment(e.target.files?.[0]); e.target.value = ""; }} />
                  <Button type="button" variant="ghost" title="Anexar arquivo ou foto" aria-label="Anexar arquivo ou foto"
                    onClick={() => fileInput.current?.click()} disabled={sending}>
                    <Paperclip className="w-4 h-4" />
                  </Button>
                  <Button type="button" variant="ghost" title="Tirar foto" aria-label="Tirar foto"
                    onClick={openCamera} disabled={sending}>
                    <Camera className="w-4 h-4" />
                  </Button>
                  <Button type="button" variant="ghost" title={recording ? "Parar gravação" : "Gravar áudio"}
                    aria-label={recording ? "Parar gravação" : "Gravar áudio"}
                    onClick={recording ? () => recorderRef.current?.stop() : startRecording}
                    disabled={sending || Boolean(voiceNote)}>
                    {recording ? <Square className="w-4 h-4 text-red-400" /> : <Mic className="w-4 h-4" />}
                  </Button>
                  {[['stage', 'Etapa'], ['products', 'Produtos'], ['followup', 'Retorno'], ['replies', 'Respostas'], ['ai', 'IA']].map(([key, label]) => (
                    <button key={key} type="button" aria-expanded={footerTool === key} onClick={() => setFooterTool(current => current === key ? '' : key)} className={`rounded-md px-2 py-2 text-xs transition-colors ${footerTool === key ? 'bg-pink-500/20 text-pink-200' : 'text-slate-400 hover:bg-slate-800 hover:text-pink-200'}`}>{label}</button>
                  ))}
                {selected.proposal_stalled && !isArchived(selected.phone) && (
                  <Button type="button" size="sm" variant="outline" onClick={() => setFooterTool("followup")} className="text-pink-300 h-8 px-2 text-xs">Retomei a proposta</Button>
                )}
                {hasReview(selected.phone) && <select aria-label="Motivo da revisão" disabled={reviewBusy} value={reviews[selected.phone]} onChange={event => toggleReview(selected.phone, event.target.value)} className="rounded border border-purple-500/50 bg-slate-900 px-2 text-xs text-purple-200">
                  <option value="">Sem motivo</option>
                  <option>Conferir documento</option>
                  <option>Rever proposta</option>
                  <option>Confirmar informação</option>
                </select>}
                <Button type="button" size="sm" variant="outline" disabled={reviewBusy} aria-pressed={hasReview(selected.phone)} onClick={() => toggleReview(selected.phone)} className="text-purple-300 h-8 px-2 text-xs">{hasReview(selected.phone) ? "Tirar da revisão" : "Marcar para revisar"}</Button>
                <Button type="button" size="sm" variant="outline" disabled={archiveBusy} onClick={() => toggleArchive(selected.phone)} className="text-purple-300 h-8 px-2 text-xs">{isArchived(selected.phone) ? "Desarquivar" : "Arquivar"}</Button>
                <Button type="button" size="sm" variant="outline" disabled={pinSaving || Boolean(stageSaving)} aria-pressed={selected.pinned === true} onClick={() => togglePin(selected)} className="text-pink-300 h-8 px-2 text-xs">
                  <Pin className="mr-1 w-3 h-3" />{pinSaving ? "Salvando..." : selected.pinned === true ? "Desafixar" : "Fixar conversa"}
                </Button>
                  {recording && <span className="text-xs text-red-300">Gravando... clique para parar</span>}
                  {attachment && <span className="text-xs text-pink-300 truncate max-w-[60%]">{attachment.name}</span>}
                  {attachment && <Button type="button" variant="ghost" size="icon" title="Remover anexo"
                    onClick={() => setAttachment(null)} disabled={sending}><X className="w-4 h-4" /></Button>}
                </div>
                {voiceNote && (
                  <div className="flex flex-wrap items-center gap-2 text-sm text-white">
                    <audio controls src={voiceNote.url} aria-label="Ouvir áudio gravado" className="h-9 max-w-full" />
                    <Button type="button" variant="ghost" onClick={() => setVoiceNote(null)} disabled={sending}>Cancelar</Button>
                    <Button type="button" onClick={sendVoiceNote} disabled={sending || voiceNote.phone !== selected?.phone} className="bg-pink-500 hover:bg-pink-600">Enviar áudio</Button>
                  </div>
                )}
                {activeQuote && <div className="flex items-start justify-between gap-2 border-l-2 border-pink-400 bg-slate-800 p-2 text-xs">
                  <div className="min-w-0"><strong>Respondendo a</strong><div className="truncate">{activeQuote.preview}</div></div>
                  <button type="button" aria-label="Cancelar resposta citada" disabled={sending} onClick={() => setQuote(null)}>×</button>
                </div>}
                <div className="flex gap-3">
                <Textarea
                  rows={2}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Escreva a mensagem..."
                  className="bg-slate-900/70 border-slate-800 resize-none focus:border-pink-500/50"
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      !e.shiftKey
                    ) {
                      e.preventDefault();

                      if (!sending && (text.trim() || attachment)) {
                        send();
                      }
                    }
                  }}
                />

                <Button
                  disabled={sending || (!text.trim() && !attachment)}
                  onClick={send}
                  className="bg-pink-500 hover:bg-pink-600 text-white px-5"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
                </div>
              </div>
              </>}
              <div className="flex gap-2 px-4 py-2 border-t border-slate-800 flex-none" aria-label="Abas da conversa">
                <Button type="button" size="sm" variant={conversationTab === "messages" ? "default" : "outline"} onClick={() => setConversationTab("messages")}>Mensagens</Button>
                <Button type="button" size="sm" variant={conversationTab === "notes" ? "default" : "outline"} onClick={() => setConversationTab("notes")}>Notas internas{internalNotes.length > 0 ? ` (${internalNotes.length})` : ""}</Button>
              </div>
            </>
          ) : (
            <div className="h-full grid place-items-center">
              <div className="text-center">
                <MessageCircle className="w-12 h-12 text-pink-500/50 mx-auto mb-3" />

                <div className="font-semibold">
                  Selecione uma conversa
                </div>

                <div className="text-sm text-slate-500 mt-1">
                  Escolha um cliente na lista ao lado.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}


const contactPhotoJobs = [];
let contactPhotoActive = 0;
function runContactPhotoJobs() {
  while (contactPhotoActive < 2 && contactPhotoJobs.length) {
    const job = contactPhotoJobs.shift();
    contactPhotoActive += 1;
    Promise.resolve().then(job).catch(() => {}).finally(() => {
      contactPhotoActive -= 1;
      runContactPhotoJobs();
    });
  }
}

function WhatsAppContactAvatar({ phone }) {
  const holder = React.useRef(null);
  const [photo, setPhoto] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    let started = false;
    const controller = new AbortController();
    const number = String(phone || "").replace(/\D/g, "");
    const start = () => {
      if (started || !alive || !/^\d{10,15}$/.test(number)) return;
      started = true;
      contactPhotoJobs.push(async () => {
        if (!alive) return;
        try {
          const { data } = await api.get("/contacts/profile-photo", {
            params: { phone: number },
            timeout: 15000,
            signal: controller.signal
          });
          if (alive && typeof data?.url === "string" && data.url.startsWith("https://")) {
            setPhoto({ phone, url: data.url });
          }
        } catch (_) {}
      });
      runContactPhotoJobs();
    };
    let observer;
    if (holder.current && typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          start();
          observer.disconnect();
        }
      }, { rootMargin: "80px" });
      observer.observe(holder.current);
    } else {
      start();
    }
    return () => {
      alive = false;
      controller.abort();
      observer?.disconnect();
    };
  }, [phone]);

  return (
    <span ref={holder} className="w-10 h-10 rounded-full overflow-hidden grid place-items-center flex-none">
      {photo?.phone === phone && photo?.url ? (
        <img src={photo.url} alt="Foto do contato" referrerPolicy="no-referrer"
          className="w-full h-full object-cover"
          onError={() => setPhoto(null)} />
      ) : (
        <User className="w-5 h-5 text-pink-400" />
      )}
    </span>
  );
}

function MessageReaction({ message }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emoji, setEmoji] = useState(null);
  const current = emoji ?? message.own_reaction ?? "";
  async function react(next) {
    setBusy(true);
    try {
      const {data} = await api.post(`/messages/${message.id}/reaction`, {emoji: next === current ? "" : next});
      setEmoji(data.emoji);
      setOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Não foi possível reagir a esta mensagem.");
    } finally { setBusy(false); }
  }
  return <div className="mb-1 flex flex-wrap items-center gap-1">
    <button type="button" aria-label="Reagir à mensagem" aria-expanded={open} className="rounded-full px-1 hover:bg-black/30" onClick={() => setOpen(!open)}>{current || "☺"}</button>
    {open && ["👍", "❤️", "😂", "😮", "😢", "🙏"].map(choice => <button type="button" key={choice} disabled={busy} aria-pressed={choice === current} aria-label={choice === current ? "Remover reação" : `Reagir com ${choice}`} className="rounded-full px-1 hover:bg-black/30" onClick={() => react(choice)}>{choice}</button>)}
  </div>;
}
